import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { DependencyType, type Item } from '@/common/api/types/items';
import {
  type NormalizedDependency,
  selectPanelDependencies,
  toInheritedItemIds,
} from '@/common/queries/dependencySource';
import { getItemFetchQueryOptions } from '@/common/queries/useGetItemFetchQuery';
import { useSuspenseGetItemStatusesQuery } from '@/common/queries/useGetItemStatusesQuery';
import { useSuspenseItemTypesQuery } from '@/common/queries/useItemTypesQuery';
import { useItemHealth } from '@/features/insights-dashboard/queries/useItemHealth';

const PANEL_QUERY_OPTIONS = {
  refetchOnMount: false,
} as const;

const useDependencyItems = (itemIds: number[]): { items: Item[]; isLoading: boolean } =>
  useQueries({
    queries: [...new Set(itemIds)].map((itemId) => ({
      ...getItemFetchQueryOptions({ itemId }),
      ...PANEL_QUERY_OPTIONS,
      placeholderData: (previousData: Item | undefined) => previousData,
    })),
    combine: (results) => ({
      items: results.map((result) => result.data).filter((item): item is Item => item != null),
      isLoading: results.some((result) => result.isLoading),
    }),
  });

export function useItemDependenciesPanelQuery(item: Item) {
  const { data: itemStatuses = [] } = useSuspenseGetItemStatusesQuery(PANEL_QUERY_OPTIONS);
  const { data: itemTypes = [] } = useSuspenseItemTypesQuery(PANEL_QUERY_OPTIONS);

  const normalized = useMemo(() => selectPanelDependencies(item.dependencies), [item.dependencies]);

  const predecessorDeps = useMemo(
    () => normalized.filter((dep) => dep.dependencyType === DependencyType.Predecessor),
    [normalized],
  );
  const successorDeps = useMemo(
    () => normalized.filter((dep) => dep.dependencyType === DependencyType.Successor),
    [normalized],
  );

  const predecessorsIds = useMemo(
    () => predecessorDeps.map((dep) => dep.dependencyItemId),
    [predecessorDeps],
  );
  const successorsIds = useMemo(
    () => successorDeps.map((dep) => dep.dependencyItemId),
    [successorDeps],
  );

  const predecessorInheritedIds = useMemo(
    () => toInheritedItemIds(predecessorDeps),
    [predecessorDeps],
  );
  const successorInheritedIds = useMemo(() => toInheritedItemIds(successorDeps), [successorDeps]);

  const dependencyItemIds = useMemo(
    () => [...predecessorsIds, ...successorsIds],
    [predecessorsIds, successorsIds],
  );

  // Fetched via the canonical per-item key (not the bulk-fetch endpoint) so that
  // any item edit elsewhere in the app — which always invalidates commonQueryKeys.item(id) —
  // refreshes this tab live instead of only after a page reload.
  const { items: dependencies, isLoading: isDependenciesLoading } =
    useDependencyItems(dependencyItemIds);

  const isPredecessorsLoading = predecessorsIds.length > 0 && isDependenciesLoading;
  const isSuccessorsLoading = successorsIds.length > 0 && isDependenciesLoading;

  useItemHealth({ itemIds: dependencyItemIds });

  const predecessors = useMemo(
    () => dependencies.filter((dep) => predecessorsIds.includes(dep.id)),
    [dependencies, predecessorsIds],
  );
  const successors = useMemo(
    () => dependencies.filter((dep) => successorsIds.includes(dep.id)),
    [dependencies, successorsIds],
  );

  return {
    dependencies,
    itemStatuses,
    itemTypes,
    predecessors,
    successors,
    predecessorInheritedIds,
    successorInheritedIds,
    isPredecessorsLoading,
    isSuccessorsLoading,
  };
}

export type { NormalizedDependency };
